import { type ReactElement } from 'react';
import { Typography, InfoOutline } from '@weareopenr/component-library';
import { type DesignAsset } from 'src/api/designPlatform/getTemplates';
import { type IMealtime } from 'src/types/mealtimes.types';
import { type IPriceScheme } from 'src/types/price.types';
import { type DesignPlatformFlowState, type IVenue } from '../types';
import styles from '../DesignPlatformExportFlow.module.scss';

interface SummaryStepProps {
  flowState: DesignPlatformFlowState;
  templates: DesignAsset[];
  mealtimes: IMealtime[];
  venues: IVenue[];
  priceSchemes: IPriceScheme[];
  hasMealtimes: boolean;
  error: string | null;
}

interface SummaryCardProps {
  number: number;
  title: string;
  children: React.ReactNode;
}

const SummaryCard = ({ number, title, children }: SummaryCardProps): ReactElement => (
  <div className={styles['summary-card']}>
    <div className={styles['summary-card-header']}>
      <div className={styles['summary-card-number']}>{number}</div>
      <Typography size="medium" weight="bold">
        {title}
      </Typography>
    </div>
    <div className={styles['summary-card-content']}>{children}</div>
  </div>
);

export const SummaryStep = ({
  flowState,
  templates,
  mealtimes,
  venues,
  priceSchemes,
  hasMealtimes,
  error,
}: SummaryStepProps): ReactElement => {
  const selectedTemplate = templates.find((t) => t.id === flowState.selectedTemplateId);
  const selectedMealtime = mealtimes.find((m) => m.mealtime_id === flowState.selectedMealtimeId);
  const selectedVenues = venues.filter((v) => flowState.selectedVenueIds.includes(v.venueId));
  const selectedPriceScheme = priceSchemes.find((p) => p.id === flowState.selectedPriceSchemeId);

  // Build the summary cards with dynamic numbering
  const summaryCards: { title: string; content: ReactElement }[] = [];

  summaryCards.push({
    title: 'Template',
    content: <Typography size="small">{selectedTemplate?.title || 'Not selected'}</Typography>,
  });

  if (hasMealtimes) {
    summaryCards.push({
      title: 'Mealtime',
      content: <Typography size="small">{selectedMealtime?.name || 'Not selected'}</Typography>,
    });
  }

  summaryCards.push({
    title: `Venues (${selectedVenues.length})`,
    content: (
      <div className={styles['venues-list']}>
        {selectedVenues.map((venue) => (
          <Typography key={venue.venueId} size="small">
            {venue.name}
          </Typography>
        ))}
      </div>
    ),
  });

  summaryCards.push({
    title: 'Menu Title',
    content: (
      <Typography size="small">
        {flowState.menuTitleMode === 'venue_name' ? 'Use Venue Name' : flowState.menuTitleOverride || 'Not set'}
      </Typography>
    ),
  });

  summaryCards.push({
    title: 'Price Scheme',
    content: <Typography size="small">{selectedPriceScheme?.name || 'Not selected'}</Typography>,
  });

  return (
    <div className={styles['summary-step']}>
      <div className={styles['summary-left']}>
        {summaryCards.map((card, index) => (
          <SummaryCard key={card.title} number={index + 1} title={card.title}>
            {card.content}
          </SummaryCard>
        ))}
      </div>

      {error && (
        <div className={styles['summary-error']}>
          <InfoOutline size="small" className={styles['error-icon']} />
          <Typography size="small" className={styles['error-text']}>
            {error}
          </Typography>
        </div>
      )}
    </div>
  );
};
